import React, { useState, useEffect, useCallback } from "react";
import { useHierarchy } from "../contexts/HierarchyContext.jsx";
import * as ghApi from "../api/github.js";

export default function GitHubSettings({ navigate }) {
  const { orgId, currentOrg, subTenants, refreshSubTenants } = useHierarchy();
  const [tab, setTab] = useState("config");
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  // Config tab state
  const [repo, setRepo] = useState("");
  const [branch, setBranch] = useState("main");
  const [token, setToken] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);

  // Tenant tab state
  const [pushing, setPushing] = useState(null); // subTenantId being pushed
  const [pushResult, setPushResult] = useState(null);

  // Pull tab state
  const [pulling, setPulling] = useState(false);
  const [pullResult, setPullResult] = useState(null);
  const [pullLogs, setPullLogs] = useState([]);
  const [expandedPull, setExpandedPull] = useState(null);

  const loadConfig = useCallback(async () => {
    if (!orgId) return;
    try {
      const cfg = await ghApi.getConfig(orgId);
      setConfig(cfg);
      setRepo(cfg.repo || "");
      setBranch(cfg.branch || "main");
      setEnabled(cfg.enabled || false);
      setToken("");
      const status = await ghApi.getStatus(orgId);
      setSyncStatus(status);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = { enabled, repo, branch };
      if (token) body.token = token;
      await ghApi.saveConfig(orgId, body);
      setToken("");
      await loadConfig();
    } catch (err) { alert("Save failed: " + err.message); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTestResult(null);
    try {
      const r = await ghApi.testConn(orgId, { repo, branch, token: token || undefined });
      setTestResult(r);
    } catch (err) { setTestResult({ success: false, error: err.message }); }
  };

  const handleCreateRepo = async () => {
    try {
      const r = await ghApi.createGHRepo(orgId, { repoName: repo.split("/").pop() || `${currentOrg?.slug}-testing` });
      setTestResult({ success: true, ...r, message: "Repository created!" });
      setRepo(r.name);
      await loadConfig();
    } catch (err) { alert("Create failed: " + err.message); }
  };

  const handlePush = async (scope, subTenantId) => {
    setPushing(subTenantId || "all");
    setPushResult(null);
    try {
      await ghApi.push(orgId, { scope, subTenantId });
      const poll = async () => {
        const s = await ghApi.getPushStatus(orgId);
        if (s.status === "complete") { setPushResult(s.result); setPushing(null); refreshSubTenants(); await loadConfig(); }
        else if (s.status === "error") { setPushResult({ error: s.error }); setPushing(null); }
        else setTimeout(poll, 2000);
      };
      setTimeout(poll, 2000);
    } catch (err) { setPushResult({ error: err.message }); setPushing(null); }
  };

  const handlePull = async () => {
    setPulling(true);
    setPullResult(null);
    try {
      const r = await ghApi.pull(orgId);
      setPullResult(r);
      await loadConfig();
      await loadPullLogs();
    } catch (err) { setPullResult({ error: err.message }); }
    finally { setPulling(false); }
  };

  const loadPullLogs = async () => {
    try { const d = await ghApi.getPullLog(orgId); setPullLogs(d.logs || []); }
    catch {}
  };

  const handleUnlock = async (subId) => {
    try {
      // Direct API call to remove github_managed flag
      const { apiPut } = await import("../api/client.js");
      // Find the org/tenant path for this sub-tenant
      const sub = subTenants.find(s => s._id === subId);
      if (!sub) return;
      await fetch(`/api/v1/github/unlock?orgId=${orgId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subTenantId: subId }),
      });
      refreshSubTenants();
    } catch (err) { alert(err.message); }
  };

  if (loading) return <div style={s.loading}>Loading...</div>;
  if (!orgId) return <div style={s.loading}>Select an organization first.</div>;

  return (
    <div>
      <div style={s.header}>
        <h1 style={s.title}>GitHub Sync</h1>
        <p style={s.subtitle}>{currentOrg?.name} — push test cases and data to GitHub</p>
      </div>

      {/* Tabs */}
      <div style={s.tabs}>
        {[
          { id: "config", label: "Configuration" },
          { id: "tenants", label: "Tenant Management" },
          { id: "pull-log", label: "Pull History" },
        ].map((t) => (
          <button key={t.id} style={tab === t.id ? s.tabActive : s.tab}
            onClick={() => { setTab(t.id); if (t.id === "pull-log") loadPullLogs(); }}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={s.body}>
        {/* ─── Configuration Tab ─────────────────────────────────────── */}
        {tab === "config" && (
          <>
            <div style={s.field}>
              <label style={s.label}>Repository (owner/repo)</label>
              <input style={s.input} value={repo} onChange={(e) => setRepo(e.target.value)} placeholder="your-org/agent-testing" />
            </div>
            <div style={s.row}>
              <div style={s.field}>
                <label style={s.label}>Branch</label>
                <input style={s.input} value={branch} onChange={(e) => setBranch(e.target.value)} placeholder="main" />
              </div>
              <div style={s.field}>
                <label style={s.label}>Enabled</label>
                <select style={s.input} value={enabled ? "yes" : "no"} onChange={(e) => setEnabled(e.target.value === "yes")}>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
            <div style={s.field}>
              <label style={s.label}>Personal Access Token</label>
              <input type="password" style={s.input} value={token} onChange={(e) => setToken(e.target.value)}
                placeholder={config?.has_token ? "Token saved (enter new to replace)" : "ghp_xxxx..."} />
              {config?.has_token && <span style={s.tokenSaved}>Saved</span>}
            </div>
            <div style={s.actions}>
              <button style={s.btnPrimary} onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save"}</button>
              <button style={s.btnOutline} onClick={handleTest}>Test Connection</button>
              <button style={s.btnOutline} onClick={handleCreateRepo}>Create Repo</button>
              <button style={s.btnOutline} onClick={handlePull} disabled={pulling}>{pulling ? "Pulling..." : "Pull from GitHub"}</button>
            </div>

            {testResult && (
              <div style={testResult.success ? s.alertOk : s.alertErr}>
                {testResult.success ? `Connected to ${testResult.name} (${testResult.private ? "private" : "public"})` : `Error: ${testResult.error}`}
                {testResult.message && <div>{testResult.message}</div>}
              </div>
            )}

            {pullResult && (
              <div style={pullResult.error ? s.alertErr : s.alertOk}>
                {pullResult.error ? `Pull error: ${pullResult.error}` :
                  `Pull complete: ${pullResult.applied} applied, ${pullResult.skipped} unchanged, ${pullResult.errors} errors (${pullResult.total_files} files)`}
              </div>
            )}

            {syncStatus && (
              <div style={s.statusTable}>
                <div style={s.statusTitle}>Sync Status</div>
                <div style={s.statusRow}><span style={s.statusLabel}>Repository</span><span>{config?.repo || "—"}</span></div>
                <div style={s.statusRow}><span style={s.statusLabel}>Branch</span><span>{config?.branch || "main"}</span></div>
                <div style={s.statusRow}><span style={s.statusLabel}>Last Push</span><span>{syncStatus.last_push ? new Date(syncStatus.last_push).toLocaleString() : "Never"}</span></div>
                <div style={s.statusRow}><span style={s.statusLabel}>Last Pull</span><span>{syncStatus.last_pull ? new Date(syncStatus.last_pull).toLocaleString() : "Never"}</span></div>
                <div style={s.statusRow}>
                  <span style={s.statusLabel}>Latest Commit</span>
                  <span>{syncStatus.latest_commit_sha ? syncStatus.latest_commit_sha.slice(0, 8) : "—"}
                    {syncStatus.is_behind && <span style={s.behindBadge}>New commits available</span>}
                  </span>
                </div>
              </div>
            )}
          </>
        )}

        {/* ─── Tenant Management Tab ─────────────────────────────────── */}
        {tab === "tenants" && (
          <>
            <div style={s.field}>
              <button style={s.btnPrimary} onClick={() => handlePush("all")} disabled={!!pushing}>
                {pushing === "all" ? "Pushing..." : "Push All to GitHub"}
              </button>
            </div>
            {pushResult && (
              <div style={pushResult.error ? s.alertErr : s.alertOk}>
                {pushResult.error ? `Error: ${pushResult.error}` :
                  `Pushed ${pushResult.files_pushed} files → ${pushResult.url} (SHA: ${pushResult.sha?.slice(0, 8)})`}
              </div>
            )}
            <div style={s.statusTitle}>Sub-Tenants</div>
            {subTenants.map((st) => (
              <div key={st._id} style={s.tenantRow}>
                <div style={s.tenantInfo}>
                  <span style={s.tenantName}>{st.name}</span>
                  <span style={s.tenantSlug}>{st.slug}</span>
                  <span style={s.specialtyBadge}>{st.specialty}</span>
                </div>
                <div style={s.tenantActions}>
                  {st.github_managed ? (
                    <>
                      <span style={s.managedBadge}>Managed by GitHub</span>
                      <button style={s.btnSmall} onClick={() => handleUnlock(st._id)}>Unlock for GUI</button>
                    </>
                  ) : (
                    <span style={s.notPushed}>Not pushed</span>
                  )}
                  <button style={s.btnSmallPrimary}
                    onClick={() => handlePush("tenant", st._id)}
                    disabled={!!pushing}>
                    {pushing === st._id ? "Pushing..." : "Push"}
                  </button>
                </div>
              </div>
            ))}
          </>
        )}

        {/* ─── Pull History Tab ──────────────────────────────────────── */}
        {tab === "pull-log" && (
          <>
            {pullLogs.length === 0 ? (
              <div style={s.empty}>No pull history yet</div>
            ) : pullLogs.map((log, i) => (
              <div key={log._id} style={s.pullCard}>
                <div style={s.pullHeader} onClick={() => setExpandedPull(expandedPull === i ? null : i)}>
                  <span style={s.pullNum}>#{pullLogs.length - i}</span>
                  <span style={s.pullSha}>{log.sha?.slice(0, 8)}</span>
                  <span style={s.pullTime}>{new Date(log.pulled_at).toLocaleString()}</span>
                  <span style={s.pullBadge}>{log.applied_count} applied</span>
                  <span style={s.pullBadgeGray}>{log.skipped_count} unchanged</span>
                  {log.error_count > 0 && <span style={s.pullBadgeRed}>{log.error_count} errors</span>}
                  <span style={s.chevron}>{expandedPull === i ? "▴" : "▾"}</span>
                </div>
                {expandedPull === i && (
                  <div style={s.pullDetails}>
                    {(log.changes || []).map((c, j) => (
                      <div key={j} style={s.changeRow}>
                        <span style={c.type === "added" ? s.typeBadgeNew : s.typeBadgeMod}>{c.type === "added" ? "NEW" : "MOD"}</span>
                        <span style={s.changePath}>{c.path}</span>
                        {c.type === "modified" && <span style={s.changeLines}>+{c.lines_added} −{c.lines_removed}</span>}
                      </div>
                    ))}
                    {(log.errors || []).map((e, j) => (
                      <div key={`e${j}`} style={s.errorRow}>{e.path}: {e.error}</div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

const s = {
  loading: { padding: 60, textAlign: "center", color: "#999" },
  header: { padding: "20px 32px", borderBottom: "1px solid #e5e5e5" },
  title: { fontSize: 22, fontWeight: 600 },
  subtitle: { fontSize: 13, color: "#999", marginTop: 4 },
  tabs: { display: "flex", gap: 2, padding: "12px 32px 0", background: "#fafafa", borderBottom: "1px solid #e5e5e5" },
  tab: { padding: "8px 16px", border: "none", borderRadius: "6px 6px 0 0", background: "transparent", color: "#666", fontSize: 13, fontWeight: 500, cursor: "pointer" },
  tabActive: { padding: "8px 16px", border: "1px solid #e5e5e5", borderBottom: "1px solid #fff", borderRadius: "6px 6px 0 0", background: "#fff", color: "#0f0f0f", fontSize: 13, fontWeight: 600, cursor: "pointer", marginBottom: -1 },
  body: { padding: "20px 32px", maxWidth: 720 },
  field: { marginBottom: 14 },
  row: { display: "flex", gap: 12 },
  label: { display: "block", fontSize: 12, fontWeight: 500, color: "#666", marginBottom: 5 },
  input: { width: "100%", padding: "8px 12px", border: "1px solid #e5e5e5", borderRadius: 6, fontSize: 13, outline: "none" },
  tokenSaved: { fontSize: 11, color: "#059669", background: "#ecfdf5", padding: "2px 6px", borderRadius: 4, marginLeft: 8 },
  actions: { display: "flex", gap: 8, marginBottom: 16 },
  btnPrimary: { padding: "8px 18px", background: "#0f0f0f", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 500 },
  btnOutline: { padding: "8px 18px", background: "#fff", color: "#0f0f0f", border: "1px solid #e5e5e5", borderRadius: 6, fontSize: 13, fontWeight: 500 },
  btnSmall: { padding: "4px 10px", background: "#fff", color: "#666", border: "1px solid #e5e5e5", borderRadius: 4, fontSize: 11 },
  btnSmallPrimary: { padding: "4px 10px", background: "#0f0f0f", color: "#fff", border: "none", borderRadius: 4, fontSize: 11 },
  alertOk: { padding: "10px 14px", background: "#ecfdf5", color: "#059669", borderRadius: 8, fontSize: 13, marginBottom: 14 },
  alertErr: { padding: "10px 14px", background: "#fef2f2", color: "#dc2626", borderRadius: 8, fontSize: 13, marginBottom: 14 },
  statusTable: { border: "1px solid #e5e5e5", borderRadius: 8, padding: 14, marginTop: 14 },
  statusTitle: { fontSize: 14, fontWeight: 600, marginBottom: 10 },
  statusRow: { display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 13, borderBottom: "1px solid #f5f5f5" },
  statusLabel: { color: "#666" },
  behindBadge: { fontSize: 10, color: "#f59e0b", background: "#fef3c7", padding: "1px 6px", borderRadius: 4, marginLeft: 6, fontWeight: 500 },
  tenantRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", border: "1px solid #e5e5e5", borderRadius: 8, marginBottom: 8 },
  tenantInfo: { display: "flex", alignItems: "center", gap: 8 },
  tenantName: { fontWeight: 600, fontSize: 14 },
  tenantSlug: { fontSize: 11, color: "#999", fontFamily: "monospace" },
  specialtyBadge: { fontSize: 10, color: "#1d4ed8", background: "#eff6ff", padding: "1px 6px", borderRadius: 4 },
  tenantActions: { display: "flex", alignItems: "center", gap: 8 },
  managedBadge: { fontSize: 10, color: "#7c3aed", background: "#f3e8ff", padding: "2px 8px", borderRadius: 4, fontWeight: 500 },
  notPushed: { fontSize: 11, color: "#999" },
  empty: { textAlign: "center", color: "#bbb", padding: "40px 0" },
  pullCard: { border: "1px solid #e5e5e5", borderRadius: 8, marginBottom: 8, overflow: "hidden" },
  pullHeader: { display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", cursor: "pointer", fontSize: 13 },
  pullNum: { fontWeight: 600, color: "#0f0f0f" },
  pullSha: { fontFamily: "monospace", fontSize: 11, color: "#999" },
  pullTime: { fontSize: 12, color: "#666" },
  pullBadge: { fontSize: 10, color: "#059669", background: "#ecfdf5", padding: "1px 6px", borderRadius: 4, fontWeight: 500 },
  pullBadgeGray: { fontSize: 10, color: "#666", background: "#f5f5f5", padding: "1px 6px", borderRadius: 4 },
  pullBadgeRed: { fontSize: 10, color: "#dc2626", background: "#fef2f2", padding: "1px 6px", borderRadius: 4 },
  chevron: { marginLeft: "auto", color: "#999", fontSize: 11 },
  pullDetails: { padding: "0 14px 12px", borderTop: "1px solid #f0f0f0" },
  changeRow: { display: "flex", alignItems: "center", gap: 8, padding: "4px 0", fontSize: 12 },
  typeBadgeNew: { fontSize: 9, fontWeight: 600, color: "#059669", background: "#ecfdf5", padding: "1px 5px", borderRadius: 3 },
  typeBadgeMod: { fontSize: 9, fontWeight: 600, color: "#f59e0b", background: "#fef3c7", padding: "1px 5px", borderRadius: 3 },
  changePath: { fontFamily: "monospace", fontSize: 11, color: "#333" },
  changeLines: { fontSize: 10, color: "#999" },
  errorRow: { padding: "4px 0", fontSize: 12, color: "#dc2626" },
};
